// assets/cart-drawer.js - Fixed quantity tracking version
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isUpdating = false;
    this.processingClicks = new Set(); // Track which variants are being processed
  }

  connectedCallback() {
    console.log('[CartDrawer] Initializing...');
    
    // Remove any existing listeners first
    this.cleanup();
    
    // Add click listener for backdrop (to close drawer)
    this.backdropClickHandler = (e) => {
      // Check if we clicked on the drawer element itself (the backdrop)
      // Not on any child elements
      if (e.target === this) {
        console.log('[CartDrawer] Backdrop clicked, closing drawer');
        this.close();
      }
    };
    
    // Use capture phase to catch clicks before they bubble
    this.addEventListener('click', this.backdropClickHandler, true);

    /* Esc key handler */
    this.escHandler = (e) => {
      if (e.key === 'Escape' && this.classList.contains('active')) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escHandler);

    /* Quantity and remove button clicks */
    this.buttonClickHandler = this.handleButtonClick.bind(this);
    this.addEventListener('click', this.buttonClickHandler, true); // Add true for capture phase
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    if (this.backdropClickHandler) {
      this.removeEventListener('click', this.backdropClickHandler, true);
    }
    
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
    }
    
    if (this.buttonClickHandler) {
      this.removeEventListener('click', this.buttonClickHandler);
    }
  }

  handleButtonClick(e) {
    // Check if we clicked on a quantity or remove button
    const button = e.target.closest('button.quantity__button, a.button--tertiary');
    if (!button) return;

    // Prevent default and stop propagation
    e.preventDefault();
    e.stopPropagation();

    // Find the cart item row
    const row = e.target.closest('tr.cart-item');
    if (!row) {
      console.error('[CartDrawer] Could not find cart item row');
      return;
    }

    // Get the variant ID from the row
    const variantId = row.dataset.variantId;
    if (!variantId) {
      console.error('[CartDrawer] Could not find variant ID on row');
      return;
    }

    // Check if we're already processing this variant
    if (this.processingClicks.has(variantId)) {
      console.log('[CartDrawer] Already processing variant:', variantId);
      return;
    }

    // Find the quantity input in this row
    const qtyInput = row.querySelector('input.quantity__input');
    if (!qtyInput) {
      console.error('[CartDrawer] Could not find quantity input');
      return;
    }


    // Get current quantity - this is the actual current value
    let currentQty = parseInt(qtyInput.value, 10);
    if (isNaN(currentQty)) currentQty = 0;
    
    let newQty = currentQty;

    // Determine action based on button type
    if (button.classList.contains('quantity__button')) {
      const action = button.getAttribute('name');
      if (action === 'plus') {
        newQty = currentQty + 1;
      } else if (action === 'minus') {
        newQty = Math.max(0, currentQty - 1);
      }
    } else if (button.classList.contains('button--tertiary')) {
      // Remove button
      newQty = 0;
    }

    console.log('[CartDrawer] Cart update:', { 
      variantId, 
      currentQty, 
      newQty,
      action: button.getAttribute('name') || 'remove'
    });

    // Only update if quantity actually changed
    if (newQty !== currentQty) {
      // Mark this variant as being processed
      this.processingClicks.add(variantId);
      
      // Update the input value visually
      qtyInput.value = newQty;
      
      // Disable this row's buttons
      this.setRowButtonsDisabled(row, true);
      
      // Update the cart
      this.updateCartLine(variantId, newQty, currentQty);
    }
  }

  /* ---------- public API ---------- */
  open() {
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    // Don't refresh on open if we're in the middle of an update
    if (!this.isUpdating && this.processingClicks.size === 0) {
      this.refreshHTML();
    }
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  /* ---------- helpers ---------- */
  setRowButtonsDisabled(row, disabled) {
    row.querySelectorAll('button.quantity__button, a.button--tertiary').forEach(btn => {
      if (disabled) {
        btn.setAttribute('disabled', 'disabled');
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.5';
      } else {
        btn.removeAttribute('disabled');
        btn.style.pointerEvents = '';
        btn.style.opacity = '';
      }
    });
  }

  setButtonsDisabled(disabled) {
    this.querySelectorAll('button.quantity__button, a.button--tertiary').forEach(btn => {
      if (disabled) {
        btn.setAttribute('disabled', 'disabled');
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.5';
      } else {
        btn.removeAttribute('disabled');
        btn.style.pointerEvents = '';
        btn.style.opacity = '';
      }
    });
  }

  async updateCartLine(variantId, quantity, previousQuantity) {
    try {
      this.isUpdating = true;
      
      // Ensure we're sending the correct data format
      const updateData = {
        id: variantId.toString(), // Ensure it's a string
        quantity: parseInt(quantity, 10) // Ensure it's a number
      };

      console.log('[CartDrawer] Sending cart update:', updateData);

      // Send the update to the cart
      const response = await fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/change.js' : '/cart/change.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const cartData = await response.json();
      console.log('[CartDrawer] Cart updated successfully:', cartData);
      
      // Publish cart update event using both methods for compatibility
      const eventData = { 
        cart: cartData, 
        variantId: variantId, 
        quantity: quantity
      };
      
      // Method 1: Use PubSub if available
      if (window.publish && window.PUB_SUB_EVENTS) {
        try {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, eventData);
        } catch (e) {
          console.warn('[CartDrawer] PubSub publish failed:', e);
        }
      }
      
      // Method 2: Always dispatch CustomEvent as backup
      try {
        const event = new CustomEvent('cart:updated', {
          detail: eventData,
          bubbles: true,
          cancelable: false
        });
        document.dispatchEvent(event);
      } catch (e) {
        console.warn('[CartDrawer] CustomEvent dispatch failed:', e);
      }
      
      // Remove from processing set
      this.processingClicks.delete(variantId);
      
      // Refresh the drawer HTML
      await this.refreshHTML();
      
    } catch (error) {
      console.error('[CartDrawer] Error updating cart:', error);
      
      // Revert the quantity on error
      const row = this.querySelector(`tr[data-variant-id="${variantId}"]`);
      if (row) {
        const input = row.querySelector('input.quantity__input');
        if (input && previousQuantity !== undefined) {
          input.value = previousQuantity;
        }
      }
      
      // Remove from processing set
      this.processingClicks.delete(variantId);
      
      // Re-enable buttons on error
      this.setButtonsDisabled(false);
      this.isUpdating = false;
    }
  }

  async refreshHTML() {
    try {
      console.log('[CartDrawer] Refreshing drawer content...');
      
      const response = await fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart?view=drawer' : '/cart?view=drawer', {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(html, 'text/html');
      const newInner = parsedDoc.querySelector('.drawer__inner');
      
      if (newInner) {
        const currentInner = this.querySelector('.drawer__inner');
        if (!currentInner) {
          console.error('[CartDrawer] Could not find .drawer__inner element');
          return;
        }
        
        // Store current scroll position
        const scrollTop = currentInner.scrollTop || 0;
        
        // Replace the inner HTML
        currentInner.innerHTML = newInner.innerHTML;
        
        // Restore scroll position
        currentInner.scrollTop = scrollTop;
        
        console.log('[CartDrawer] Content refreshed successfully');
        
        // Clear all processing flags
        this.processingClicks.clear();
        this.isUpdating = false;
        
        // Re-enable all buttons
        this.setButtonsDisabled(false);
        
        // Note: We don't need to re-initialize quantity-input elements
        // because they handle their own initialization via connectedCallback
      } else {
        console.error('[CartDrawer] Could not find .drawer__inner in response');
        this.isUpdating = false;
        this.processingClicks.clear();
      }
    } catch (error) {
      console.error('[CartDrawer] Error refreshing cart drawer:', error);
      this.setButtonsDisabled(false);
      this.isUpdating = false;
      this.processingClicks.clear();
    }
  }
}

/* define once even if the file is imported twice by mistake */
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/* -------- global helpers so legacy code keeps working -------- */
window.openCartDrawer = () => {
  const drawer = document.querySelector('cart-drawer');
  if (drawer) drawer.open();
};

window.closeCartDrawer = () => {
  const drawer = document.querySelector('cart-drawer');
  if (drawer) drawer.close();
};